/**
 * Текст сообщения «Чатов» с кликабельными ссылками и номерами — как цифры в crm-front.
 *
 * Что присылают (выборка сообщений за 30 дней, 22.10.26): телефон клиента — «89153953742»,
 * «+7 473 205-22-62»; сделку — голыми цифрами «1252770», форматом CRM «С-1252-832» (бывает слитно:
 * «СделкаС-1246-616») и «1248-944»; серию — «473709», «артикул-серия» «253865-474036» и «250936=465249».
 * Ясное открываем сразу: телефон — клиента (поиск номера, как всплывашка «Входящий»), сделку — её чат,
 * серию — карточку. Голые 6–7 цифр — и сделка, и серия, и лид (серии CRM — 290000…9999999): по клику
 * сервер говорит, что есть, одно — открываем, несколько — окно «что открыть».
 * Спека `docs/specs/2026-10-22-chat-numbers-design.md`.
 */
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import { type NumberFound, type NumberItem, type NumberKind, loadChatNumber } from "@/lib/clientChats";

/** Открыть клиента по телефону — `openIncoming` экрана «Чатов»: номер в поиск, его чат или карточка. */
export const OpenPhone = createContext<((phone: string) => void) | null>(null);

type Token =
  | { kind: "url"; text: string; href: string }
  | { kind: "phone"; text: string; phone: string }
  | { kind: "serial"; text: string; serial: string }
  | { kind: "number"; text: string; n: string; kinds: NumberKind[] };
type Part = string | Token;

// Порядок важен: ссылка забирает цифры внутри себя, формат CRM — раньше голых цифр.
const RE = new RegExp(
  [
    String.raw`(?<url>(?:https?:\/\/|www\.)[^\s<>"']*[^\s<>"'.,;:!?)\]}»])`,
    // «С-1252-832» (кириллица или латиница), «С-1252832»; строчная — только с группами.
    String.raw`(?<dealf>[СC]-\s?\d{1,4}-\d{3}(?!\d)|[сc]-\d{3,4}-\d{3}(?!\d)|[СC]-\d{5,8}(?![\d-]))`,
    String.raw`(?<!\d)\d{6}[-=](?<art>\d{6,7})(?![\d-])`,
    // «1248-944» — сделка CRM без «С-»; «2000-4000» (цены) и «42-44» (размеры) сюда не попадают.
    String.raw`(?<![\d\-.,:/])(?<deal>1\d{3}-\d{3})(?![\d\-.,:/]?\d)`,
    String.raw`(?<![\w+])(?<phone>(?:\+7|8|7)[\s(-]{0,2}\d{3}[\s)-]{0,2}\d{3}[\s-]?\d{2}[\s-]?\d{2}|9\d{9})(?!\d)`,
    // Голые 6–7 цифр, кроме сумм («120000₽», «150000 руб») и кусков дат, адресов, отметок.
    String.raw`(?<![\w\-.,:/#@+])(?<bare>\d{6,7})(?!\d|[.,:/-]\d|\s?(?:₽|р\.|р(?![а-яё])|руб|тыс|т\.р))`,
  ].join("|"),
  "giu",
);

const digits = (s: string) => s.replace(/\D/g, "");

/** Текст → куски: строки как есть, найденное — токенами. Чистая, её проверяет тест. */
export function splitText(text: string): Part[] {
  const parts: Part[] = [];
  let at = 0;
  for (const m of text.matchAll(RE)) {
    const g = m.groups!;
    const s = m[0];
    let t: Token;
    if (g.url) t = { kind: "url", text: s, href: s.startsWith("www.") ? `https://${s}` : s };
    else if (g.dealf || g.deal) t = { kind: "number", text: s, n: digits(s), kinds: ["deal"] };
    else if (g.art) t = { kind: "serial", text: s, serial: g.art };
    else if (g.phone) {
      const d = digits(s);
      t = { kind: "phone", text: s, phone: d.length === 10 ? `7${d}` : `7${d.slice(1)}` };
    } else t = { kind: "number", text: s, n: s, kinds: ["deal", "lead", "serial"] };
    if (m.index! > at) parts.push(text.slice(at, m.index));
    parts.push(t);
    at = m.index! + s.length;
  }
  if (at < text.length) parts.push(text.slice(at));
  return parts;
}

export function MessageText({ text, out }: { text: string; out: boolean }) {
  const link = `cursor-pointer underline decoration-1 underline-offset-2 ${
    out ? "decoration-current/40 hover:decoration-current" : "text-[var(--tg-primary)] decoration-[var(--tg-primary)]/40 hover:decoration-[var(--tg-primary)]"
  }`;
  return (
    <>
      {splitText(text).map((p, i) =>
        typeof p === "string" ? (
          p
        ) : p.kind === "url" ? (
          <a key={i} href={p.href} target="_blank" rel="noreferrer" className={`${link} break-all`} onDoubleClick={(e) => e.stopPropagation()}>
            {p.text}
          </a>
        ) : (
          <NumberLink key={i} token={p} className={link} />
        ),
      )}
    </>
  );
}

/**
 * Открыть найденное по номеру: серия — карточка, иначе чат клиента → телефон (`openPhone`) → карточка
 * сделки или лида в новой вкладке. Без `openPhone` (лента вне «Чатов»: `DealSide` «Доставок»,
 * «Невозвратов») — всё в новой вкладке. `open` вернёт false, если открыть нечего (договор или клиент
 * CRM без сделки и телефона). Им же пользуется поиск «Чатов» по номеру.
 */
export function useOpenNumber(openPhone: ((phone: string) => void) | null) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [, setParams] = useSearchParams();
  const away = !openPhone;
  const tab = (url: string) => window.open(url, "_blank", "noopener");
  const showSerial = (serial: string | number) =>
    away
      ? tab(`/chats?serial=${serial}`)
      : setParams((prev) => {
          const p = new URLSearchParams(prev);
          p.set("serial", String(serial));
          return p;
        });
  const openPhoneOf = (phone: string) => (openPhone ? openPhone(phone) : tab(`/search?q=${phone}`));
  const open = (item: NumberItem): boolean => {
    if (item.kind === "serial") showSerial(item.id);
    else if (item.chat_id) {
      if (away) tab(`/chats/${item.chat_id}`);
      else {
        const p = new URLSearchParams(search);
        ["serial", "dial"].forEach((k) => p.delete(k));
        navigate({ pathname: `/chats/${item.chat_id}`, search: p.toString() });
      }
    } else if (item.phone) openPhoneOf(item.phone);
    else if (item.kind === "deal" || item.kind === "lead") tab(`/${item.kind === "deal" ? "deals" : "leads"}/${item.id}`);
    else if (item.deal_id) tab(`/deals/${item.deal_id}`);
    else return false;
    return true;
  };
  return { open, showSerial, openPhoneOf };
}

function NumberLink({ token, className }: { token: Exclude<Token, { kind: "url" }>; className: string }) {
  const openPhone = useContext(OpenPhone);
  const client = useQueryClient();
  const ref = useRef<HTMLButtonElement>(null);
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState<NumberFound | null>(null);
  const opener = useOpenNumber(openPhone);
  const { showSerial, openPhoneOf } = opener;
  const open = (item: NumberItem) => {
    setFound(null);
    opener.open(item);
  };
  const click = async () => {
    if (token.kind === "phone") return openPhoneOf(token.phone);
    if (token.kind === "serial") return showSerial(token.serial);
    if (busy) return;
    setBusy(true);
    try {
      const r = await client.fetchQuery({
        queryKey: ["chat-number", token.n, token.kinds],
        queryFn: () => loadChatNumber(token.n, token.kinds),
        staleTime: 60_000,
      });
      if (r.items.length === 1 && !r.errors.length) open(r.items[0]!);
      else setFound(r);
    } catch {
      setFound({ n: Number(token.n), items: [], errors: ["Не удалось проверить номер — попробуйте ещё раз"] });
    } finally {
      setBusy(false);
    }
  };
  const hint =
    token.kind === "phone"
      ? "Открыть чат клиента с этим номером"
      : token.kind === "serial"
        ? `Карточка серии ${token.serial}`
        : token.kinds.length === 1
          ? "Открыть чат этой сделки"
          : "Что это за номер: сделка, лид или серия — открыть";
  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={click}
        onDoubleClick={(e) => e.stopPropagation()}
        title={hint}
        aria-busy={busy || undefined}
        className={`${className} inline p-0 text-left font-[inherit] ${busy ? "cursor-wait opacity-60" : ""}`}
        data-annot="номер в сообщении"
      >
        {token.text}
      </button>
      {found && ref.current && <NumberMenu anchor={ref.current} found={found} onPick={open} close={() => setFound(null)} />}
    </>
  );
}

const WIDTH = 300;

function NumberMenu({
  anchor,
  found,
  onPick,
  close,
}: {
  anchor: HTMLElement;
  found: NumberFound;
  onPick: (item: NumberItem) => void;
  close: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [at] = useState(() => anchor.getBoundingClientRect());
  useEffect(() => {
    const away = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!box.current?.contains(target) && !anchor.contains(target)) close();
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [anchor, close]);

  const width = Math.min(WIDTH, window.innerWidth - 16);
  const left = Math.max(8, Math.min(at.left, window.innerWidth - width - 8));
  const up = at.bottom + 220 > window.innerHeight && at.top > 220;
  const place = up ? { bottom: window.innerHeight - at.top + 6 } : { top: at.bottom + 6 };
  return createPortal(
    <div
      ref={box}
      role="dialog"
      aria-label={`Номер ${found.n}`}
      onClick={(e) => e.stopPropagation()}
      style={{ left, width, ...place }}
      className="fixed z-50 flex flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg p-2 text-[13px] leading-snug text-fg shadow-[0_8px_30px_rgba(0,0,0,.22)]"
      data-annot="что открыть по номеру"
    >
      <div className="flex items-baseline gap-2 px-1.5 pb-0.5">
        <span className="font-semibold">
          {found.items.length > 1 ? `${found.n} — что открыть?` : `Номер ${found.n}`}
        </span>
        <button type="button" onClick={close} title="Закрыть" className="ml-auto self-center text-fg-muted hover:text-fg">
          <X size={15} />
        </button>
      </div>
      {found.items.map((item) => (
        <button
          key={`${item.kind}-${item.id}`}
          type="button"
          onClick={() => onPick(item)}
          className="flex flex-col rounded-md px-1.5 py-1 text-left hover:bg-[var(--tg-hover)]"
        >
          <span className="font-medium">
            {item.title}
            <span className="font-normal text-fg-muted">
              {" — "}
              {item.kind === "serial" ? "карточка серии" : item.chat_id ? "чат клиента" : item.phone ? "клиент по номеру" : "карточка"}
            </span>
          </span>
          {item.sub && <span className="text-[12px] text-fg-muted">{item.sub}</span>}
        </button>
      ))}
      {!found.items.length && !found.errors.length && (
        <div className="px-1.5 pb-1 text-fg-muted">Не нашли сделку, лид или серию с таким номером</div>
      )}
      {found.errors.map((e) => (
        <div key={e} className="px-1.5 pb-1 text-[12px] text-err">
          {e.startsWith("CRM") ? "CRM не ответила — проверили не всё" : e}
        </div>
      ))}
    </div>,
    document.body,
  );
}
